import * as React from "react"
import Head from "next/head"
import { useRouter } from "next/router"
import {
	getCanonicalUrl,
	getHreflangAlternates,
	getListingPath,
	getSiteOrigin,
	isHomePage,
	rewriteToSiteOrigin,
} from "@/lib/seo"
import {
	buildBreadcrumbList,
	buildHomeGraph,
	serializeJsonLd,
} from "@/lib/structured-data"
// import { useI18n } from "@/hooks"
// import { ErrorPage } from "@/globals"
import { TemplatesMapping } from "@/runtime/nodes-templates"
import { NodeParamsMapping } from "@/runtime/nodes-params"
import { logger } from "../lib/logger"
import { getDefaultLanguage, getEnabledMenus } from "../lib/utils"
import { getTranslations } from "../lib/get-i18n"
import { getMenus } from "../lib/get-menus"
import { getSession } from "next-auth/react"
import { drupal } from "../lib/drupal"
import { DEFAULT_JSONAPI_NODE_PARAMS } from "../lib/const"
import { redis } from "@/lib/cache"
import { query as queryBuild } from "@/lib"
import { toNextRedirect } from "@/lib/redirects"
import {
	createTermSlugsFetcher,
	getListingRootPath,
	hasUnknownRouteFilter,
} from "@/lib/route-filters"
import { getErrorStatus, isBackendUnavailable } from "@/lib/errors"
//import { useEffect, useState } from "react"
export const config = { amp: "hybrid" }
import { readOfflineData, updateOfflineData, checkDrupalStatus } from "@/lib/offline"

const enabledMenus = getEnabledMenus()

// next-auth session cookie (chunked as `.0`, `.1`… when large).
const SESSION_COOKIE_RE = /(?:^|;\s*)(?:__Secure-)?next-auth\.session-token(?:\.\d+)?=/

// Anonymous HTML is cacheable at the edge (Cloudflare); authenticated HTML never is.
const PUBLIC_CACHE_CONTROL = "public, s-maxage=10, stale-while-revalidate=59"
const PRIVATE_CACHE_CONTROL = "private, no-cache, no-store, max-age=0, must-revalidate"

// Metatags rebuilt by NodePageHtml instead of being copied from Drupal.
const REBUILT_METATAG_IDS = ["title", "canonical_url", "og_url", "shortlink", "robots"]

export const NodePageHtml = ({ children, node, systemRoute, noindex = false }) => {
	const router = useRouter()
	// "default" is the technical locale of paths without language prefix.
	const locale = router.locale === "default" ? getDefaultLanguage() : router.locale
	const listingPath = getListingPath({ systemRoute, locale, asPath: router.asPath })
	const internal_metatags = node?.internal_metatag || []
	const metatag_title = internal_metatags.find((tag) => tag.id === "title")
	const node_page_title =
		metatag_title?.attributes?.content?.length > 0
			? metatag_title.attributes.content
			: node?.title
	const drupalCanonical = internal_metatags.find((tag) => tag.id === "canonical_url")
		?.attributes?.href
	const drupalRobots =
		internal_metatags.find((tag) => tag.id === "robots")?.attributes?.content || ""
	const translations = node?.internal_extra?.translations || {}
	// A noindex page gets neither canonical nor hreflang (contradictory signals).
	const isNoindex = noindex || /noindex/i.test(drupalRobots)

	const canonicalUrl = getCanonicalUrl({
		drupalCanonical,
		locale,
		translations,
		listingPath,
	})
	const isHome = isHomePage({ drupalCanonical, translations, locale })
	const alternates = isNoindex
		? []
		: getHreflangAlternates({ translations, locale, canonicalUrl, isHome })

	// Structured data only on indexable pages with a canonical URL.
	const siteOrigin = getSiteOrigin()
	const jsonLd =
		isNoindex || !canonicalUrl
			? []
			: [
					isHome
						? buildHomeGraph({ origin: siteOrigin })
						: buildBreadcrumbList({
								crumbs: node?.internal_breadcrumb,
								current: { name: node?.title, url: canonicalUrl },
								origin: siteOrigin,
						  }),
			  ].filter(Boolean)
	const robots = noindex ? "noindex, follow" : drupalRobots

	const metatags = internal_metatags
		.filter((tag) => !REBUILT_METATAG_IDS.includes(tag.id))
		.map((tag) => {
			const attributes = { ...tag.attributes }
			// Absolute URLs (og:image…) must point to the front origin, never to the
			// backend, the bare host or the former brand domain.
			for (const attribute of ["href", "content"]) {
				if (attribute in attributes) {
					attributes[attribute] = rewriteToSiteOrigin(attributes[attribute])
				}
			}
			return { ...tag, attributes }
		})

	return (
		<React.Fragment>
			<Head>
				<title>{node_page_title}</title>
				{robots && <meta name="robots" content={robots} />}
				{canonicalUrl && !isNoindex && <link rel="canonical" href={canonicalUrl} />}
				{canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
				{alternates.map((alternate) => (
					<link
						key={`hreflang-${alternate.hrefLang}`}
						rel="alternate"
						hrefLang={alternate.hrefLang}
						href={alternate.href}
					/>
				))}
				{jsonLd.map((data, index) => (
					<script
						key={`jsonld-${index}`}
						type="application/ld+json"
						dangerouslySetInnerHTML={{ __html: serializeJsonLd(data) }}
					/>
				))}
				{/* // TODO: create a MetaTags component */}
				{metatags.map((tag, index) => {
					const Tag = tag.tag
					return <Tag key={tag.id || index} {...tag.attributes} />
				})}
			</Head>
			{children}
		</React.Fragment>
	)
}

// @todo: refactor this and extract head, leave  NodeComponent as injected prop
export default function NodePage({ node, systemRoute = {}, ...pageProps }) {
	// const { t } = useI18n()
	let nodeType = node.type

	// System routes take priority here.
	if (systemRoute?._route === "error_page") {
		nodeType = "error_page"
	}

	if (systemRoute?._route === "maintenance_page") {
		nodeType = "maintenance_page"
	}

	const NodeComponent = TemplatesMapping[nodeType] || NodeDefault
	// Error and maintenance responses must never be indexed.
	const noindex =
		nodeType === "error_page" ||
		nodeType === "maintenance_page" ||
		Number(systemRoute?.status) >= 400
	return (
		<NodePageHtml node={node} systemRoute={systemRoute} noindex={noindex}>
			<NodeComponent node={node} systemRoute={systemRoute} {...pageProps} />
		</NodePageHtml>
	)
}

const NodeDefault = ({ node }) => {
	return (
		<div className="bg-white">
			<div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
				<div className="text-center">
					<h2 className="text-base font-semibold text-indigo-600 tracking-wide uppercase">
						Ops !
					</h2>
					<h1 className="mt-1 text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
						{node?.title}
					</h1>
					<div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-14">
						<p className="text-sm text-yellow-700">
							No template found for <strong>{node?.type}</strong> !!. Make sure you have
							defined <i>module.json</i> in your package.
						</p>
					</div>
				</div>
			</div>
		</div>
	)
}

// The backend API is unreachable: 503 + Retry-After tells crawlers the outage
// is temporary (a 500 on every URL could get pages dropped from the index).
const MAINTENANCE_STATUS = 503
const MAINTENANCE_RETRY_AFTER_SECONDS = "600"

// Unknown listing filters (/je-veux-un-conseil/{theme}…), cached term slugs.
const fetchTermSlugs = createTermSlugsFetcher({
	cache: redis,
	fetchJson: async (path) => {
		const response = await drupal.fetch(path, { noProxy: true })
		if (!response?.ok) {
			throw new Error(`Taxonomy fetch failed (${response?.status}): ${path}`)
		}
		return response.json()
	},
})

const normalizeLocale = (locale) => (locale === "default" ? getDefaultLanguage() : locale)

/**
 * Props of an error page (rendered by ErrorNode or MaintenanceNode) and the
 * matching HTTP status. Maintenance = backend outage → 503 + Retry-After.
 */
const errorPageResponse = (
	context,
	{ status, session = null, i18n = [], menus = [], locale, offlineData }
) => {
	const isMaintenance = status === MAINTENANCE_STATUS
	context.res.statusCode = status
	if (isMaintenance) {
		context.res.setHeader("Retry-After", MAINTENANCE_RETRY_AFTER_SECONDS)
	}
	// Server errors and authenticated pages must never be cached by Cloudflare; anonymous 4xx can, briefly.
	context.res.setHeader(
		"Cache-Control",
		status >= 500 || session ? PRIVATE_CACHE_CONTROL : PUBLIC_CACHE_CONTROL
	)
	return {
		props: {
			session,
			node: {},
			systemRoute: { _route: isMaintenance ? "maintenance_page" : "error_page", status },
			params: null,
			i18n,
			menus,
			locale,
			error: { statusCode: status },
			isOffline: offlineData?.isOffline ?? false,
			offlineMessage: offlineData?.offlineMessage ?? "",
		},
	}
}

const statusForError = (err) =>
	isBackendUnavailable(err) ? MAINTENANCE_STATUS : getErrorStatus(err)

/**
 * 404 page with the site chrome (translations, menus), for routes handled
 * outside of the Drupal router (e.g. pages/user/[...slug]).
 */
export async function getNotFoundServerSideProps(context) {
	const offlineData = await readOfflineData()
	const locale = normalizeLocale(context.locale)
	let i18n = [],
		menus = []
	try {
		i18n = await getTranslations(locale, { uid: 0, locale })
		menus = await getMenus(enabledMenus, locale, { uid: 0, locale })
	} catch (err) {
		logger.error("next-not-found", err)
	}
	return errorPageResponse(context, { status: 404, i18n, menus, locale, offlineData })
}

export async function getServerSideProps(context) {
	// Read current offline data
	const offlineData = await readOfflineData()

	// Check Drupal status and update offline flag if needed
	const isDrupalUp = await checkDrupalStatus()
	const shouldBeOffline = !isDrupalUp

	// Only update the file if the status has changed
	if (offlineData.isOffline !== shouldBeOffline) {
		offlineData.isOffline = shouldBeOffline
		await updateOfflineData(offlineData)
		console.log(`Offline status updated: ${shouldBeOffline ? "OFFLINE" : "ONLINE"}`)
	}

	const { slug, ...query } = context.query
	// "default" is the technical locale of paths without language prefix
	// (i18n.defaultLocale): the proxy redirects them, but paths it does not
	// match (e.g. /old.html) are still rendered, as a 404 in the default language.
	const locale = normalizeLocale(context.locale)
	let joinedSlug = Array.isArray(slug) ? slug.join("/") : slug

	// Drupal router doesn't accept empty slug.
	if (!joinedSlug) {
		joinedSlug = "/"
	}

	let accessToken = null
	let uid = 0 // Anonymous
	// getSession() does an HTTP round-trip to /api/auth/session: skip it for anonymous visitors.
	const hasSessionCookie = SESSION_COOKIE_RE.test(context.req?.headers?.cookie || "")
	const session = hasSessionCookie ? await getSession({ req: context.req }) : null

	if (session) {
		accessToken = session.accessToken
		uid = session?.user?.id || 0
	}

	let i18n = [],
		menus = []
	try {
		;[i18n, menus] = await Promise.all([
			getTranslations(locale, {
				accessToken,
				uid,
				locale,
			}),
			getMenus(enabledMenus, locale, {
				auth: session,
				uid,
				locale,
			}),
		])
	} catch (err) {
		logger.error("next-router", err)
		return errorPageResponse(context, {
			status: statusForError(err),
			session,
			i18n,
			menus,
			locale,
			offlineData,
		})
	}

	// const locale = getLocaleFromPath(joinedSlug, enabledLanguages)
	// const langprefix = locale ? `${locale}/` : ``
	// Router stuff
	let router, routerResponse
	try {
		let routerOptions = {
			// headers: {
			// 	"X-Internal-Cacheability-Debug": "true"
			// }
			// withCache: false,
			// cacheKey: `route-${uid}-${locale}-${joinedSlug}`,
		}

		if (accessToken) {
			routerOptions["withAuth"] = () => `Bearer ${accessToken}`
			routerOptions["headers"] = {
				"X-Auth-Provider": session.provider,
			}
		}

		// routerResponse = await drupal.getRoute(joinedSlug, locale, routerOptions)
		// router = await routerResponse.json()
		const cacheKey = `router:${joinedSlug} language:${locale} ${uid}`
		const cached = await redis.get(cacheKey)
		if (cached) {
			router = JSON.parse(cached)
		} else {
			routerResponse = await drupal.getRoute(joinedSlug, locale, routerOptions)
			router = await routerResponse.json()
			await redis.set(cacheKey, JSON.stringify(router), "EX", 60 * 10) // 5 mins > keep routing longer
		}
	} catch (err) {
		logger.error("next-router", err)
		return errorPageResponse(context, {
			status: statusForError(err),
			session,
			i18n,
			menus,
			locale,
			offlineData,
		})
	}

	// Check for redirect.
	if (router?.redirect?.length) {
		const [redirect] = router.redirect
		// Drupal sends the status as a number (301): a strict string comparison
		// turned every permanent redirect into a temporary 307.
		const nextRedirect = toNextRedirect(redirect)
		if (nextRedirect) {
			return { redirect: nextRedirect }
		}
	}

	// Filtered listings (/je-veux-un-conseil/{theme}, /contacts-utiles/{type}/
	// {pays}) resolve whatever the filter values: an unknown value is
	// redirected to the unfiltered listing (wrong links keep working).
	if (await hasUnknownRouteFilter(router?.system, fetchTermSlugs)) {
		const listingRoot = getListingRootPath(`/${locale}/${joinedSlug}`, router?.system)
		if (listingRoot) {
			return { redirect: { destination: listingRoot, statusCode: 301 } }
		}
		return errorPageResponse(context, {
			status: 404,
			session,
			i18n,
			menus,
			locale,
			offlineData,
		})
	}

	// Set HTTP status code.
	context.res.statusCode = router.status

	let nodeParams = Object.assign(
		{},
		JSON.parse(JSON.stringify(NodeParamsMapping[router.jsonapi.resourceName]))
	)
	// Add internal fields
	if (nodeParams?.fields && nodeParams?.fields[router.jsonapi.resourceName]) {
		// console.log("#DEBUG BEFORE", nodeParams.fields[router.jsonapi.resourceName])
		nodeParams.fields[router.jsonapi.resourceName] =
			DEFAULT_JSONAPI_NODE_PARAMS + "," + nodeParams.fields[router.jsonapi.resourceName]
		// console.log("#DEBUG AFTER", nodeParams.fields[router.jsonapi.resourceName])
	}

	let queryParams = ""
	if (Object.keys(query).length > 0) {
		queryParams = queryBuild(query)
		nodeParams["q"] = {
			...query,
		}
	}

	if (router?.system?._query) {
		nodeParams["q"] = {
			...nodeParams["q"],
			...router?.system?._query,
		}
	}

	let node
	try {
		let nodeOptions = {
			withCache: false,
			cacheKey: `node:${router.entity.id} user:${uid} language:${locale} locale routes route_match http_response`, // @todo: access token ?
		}

		if (accessToken) {
			nodeOptions["withAuth"] = () => `Bearer ${accessToken}`
			nodeOptions["headers"] = {
				"X-Auth-Provider": session.provider,
			}
		}

		// node = await drupal.getNode(router, nodeParams, locale, joinedSlug, nodeOptions)
		let joinedSlugKey = queryParams == "" ? joinedSlug : `${joinedSlug}?${queryParams}`
		joinedSlugKey = `/${joinedSlugKey}`.replace("//", "/")
		const cacheKey = `node:${router.entity.id} bundle:${router.entity.bundle} language:${locale} user:${uid} slug:${joinedSlugKey}`
		const cached = await redis.get(cacheKey)
		if (cached) {
			node = JSON.parse(cached)
		} else {
			node = await drupal.getNode(router, nodeParams, locale, joinedSlug, nodeOptions)
			if (!node.internal_extra.cache_exclude) {
				await redis.set(cacheKey, JSON.stringify(node), "EX", 60 * 5) // 1 min > keep it fresh
			}
		}
		// @todo: invoke hooks here

		context.res.setHeader(
			"Cache-Control",
			session ? PRIVATE_CACHE_CONTROL : PUBLIC_CACHE_CONTROL
		)

		return {
			props: {
				session,
				node: node,
				params: Object.keys(query).length > 0 ? query : null,
				i18n: i18n,
				menus: menus,
				locale: locale,
				systemRoute: {
					...(router?.system || {}),
					status: router.status,
				},
				isOffline: offlineData.isOffline,
				offlineMessage: offlineData.offlineMessage,
			},
		}
	} catch (err) {
		logger.error("next-node", err)
		return errorPageResponse(context, {
			status: statusForError(err),
			session,
			i18n,
			menus,
			locale,
			offlineData,
		})
	}
}
